/* ls-stade-motion-carousel — "Bien plus qu'un match" on the native aiconeo section (p70).
 *
 * Motion only. The content is the Neo document's own elements; this file
 * re-parents them into runtime layers while the scene animates and never
 * writes or copies a word of content.
 *
 * Native structure it recognises (element TYPE and order, never text/ids):
 *   section
 *     content-slider (exactly one, classic layout, ≥2 slides)
 *       slide n = ONE image element (usually image-with-children: photo +
 *                 scrim + content layer). Slide 1's layer holds the section
 *                 title, slides 2..n a title + text. A slide without children
 *                 simply has no caption.
 *     images outside the slider (the "collage" row, any count). The first six
 *       take the six tilted spots around the stage; any beyond six are hidden
 *       while the motion runs (they stay in the static page).
 *   Anything else outside the slider (heading, text, button, video, slot) =
 *   a structure this scene does not know → bail, the native page stays.
 *
 * Runtime layers (content-free wrappers, appended to the section; the native
 * `.aico-neo-section__outer` — which keeps the emptied slider shell — is
 * hidden while the motion is on, so the slider's arrows/dots/swipe/keys have
 * nothing to act on and its IntersectionObserver keeps any autoplay paused):
 *   div.lsm-car
 *     div.lsm-car__collage   ← the collage image elements (.lsm-car__snap)
 *     div.lsm-car__stage     ← scaled 1 → fullscreen
 *       div.lsm-car__track   ← the slide image elements (.lsm-car__slide), panned
 *       div.lsm-car__veil    (aria-hidden; phones only: the darker scrim)
 *     div.lsm-car__captions  ← each slide's native content layer, un-scaled
 *     div.lsm-car__peek      (aria-hidden; the blue next-section peek)
 *
 * Choreography = assets/ls-stade-carousel.js (the original section):
 *   1. approach — unpinned: the snapshots drift into their resting spots.
 *   2. master   — pinned at the header: morph (1 unit: stage scales to cover,
 *                 snapshots fly out + fade, peek fades in), then one unit per
 *                 remaining slide (track pans by xPercent, captions swap
 *                 out-then-in at each boundary). Phones: shorter pin + snap
 *                 to the resting states (directional/inertia off).
 * Built inside gsap.matchMedia (750px split), so crossing the breakpoint
 * reverts and rebuilds; geometry is function-based + invalidateOnRefresh.
 */
(function () {
  'use strict';
  if (!window.LsStadeMotion) return;

  var TILTS = [6.8, -14.9, -12.1, 10.2, 15.4, -14.8];
  var SPOTS = TILTS.length;
  var SWAP = 0.18;

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function elementChildren(node) {
    return toArray(node && node.children).filter(function (c) {
      return c.tagName !== 'STYLE' && c.tagName !== 'SCRIPT';
    });
  }

  function headerOffset() {
    var header = document.querySelector('.shopify-section-header, .section-header');
    return header ? Math.round(header.getBoundingClientRect().height) : 0;
  }

  /* The slide's image element: the slide's only element, possibly behind the
     renderer's anonymous `div[id]:not([class])` wrapper. */
  function slideImage(slide) {
    var kids = elementChildren(slide);
    if (kids.length !== 1) return null;
    var el = kids[0];
    if (!el.classList.contains('content-type-neo-image') && el.tagName === 'DIV' && !el.className) {
      kids = elementChildren(el);
      if (kids.length !== 1) return null;
      el = kids[0];
    }
    return el.classList.contains('content-type-neo-image') ? el : null;
  }

  function recognise(section, ctx) {
    var sliders = ctx.items('slider');
    if (sliders.length !== 1) return null;
    var slider = sliders[0];
    if (slider.getAttribute('data-neo-slider-layout') === 'rail') return null;
    var sliderTrack = slider.querySelector('.neo-slider__track');
    if (!sliderTrack) return null;
    var slideEls = toArray(sliderTrack.children).filter(function (c) {
      return c.classList.contains('neo-slider__slide');
    });
    if (slideEls.length < 2) return null;

    var slides = [];
    for (var i = 0; i < slideEls.length; i++) {
      var img = slideImage(slideEls[i]);
      if (!img) return null;
      var photo = null;
      var layer = null;
      elementChildren(img).forEach(function (c) {
        if (!photo && c.tagName === 'IMG') photo = c;
        if (!photo && c.tagName === 'A' && c.querySelector('img')) photo = c.querySelector('img');
        if (c.classList.contains('neo-image__overlay')) layer = c;
      });
      if (!photo) return null;
      // An empty content layer is no caption.
      if (layer && !elementChildren(layer).length) layer = null;
      slides.push({ el: img, photo: photo, layer: layer });
    }

    function outside(el) {
      return !slider.contains(el);
    }
    var collage = ctx.items('image').filter(function (el) {
      return outside(el) && !(el.parentElement && el.parentElement.closest('.content-type-neo-image'));
    });
    function inCollage(el) {
      return collage.some(function (c) { return c.contains(el); });
    }
    var unknown = ctx.items('heading').concat(ctx.items('text'), ctx.items('button'), ctx.items('video'), ctx.items('slot'))
      .filter(function (el) { return outside(el) && !inCollage(el); });
    if (unknown.length) return null;

    var outer = null;
    elementChildren(section).forEach(function (c) {
      if (c.classList.contains('aico-neo-section__outer')) outer = c;
    });
    if (!outer) return null;

    return { slider: slider, slides: slides, collage: collage };
  }

  function div(className, decorative) {
    var d = document.createElement('div');
    d.className = className;
    if (decorative) d.setAttribute('aria-hidden', 'true');
    return d;
  }

  LsStadeMotion.scene('carousel', function (section, ctx) {
    if (ctx.reduced || !ctx.mm) return null;
    var found = recognise(section, ctx);
    if (!found) return null;

    var slides = found.slides;
    var count = slides.length;
    var moved = [];
    var eager = [];

    var root = div('lsm-car');
    var collageLayer = div('lsm-car__collage');
    var stage = div('lsm-car__stage');
    var track = div('lsm-car__track');
    var veil = div('lsm-car__veil', true);
    var captionsLayer = div('lsm-car__captions');
    var peek = div('lsm-car__peek', true);
    var snaps = [];
    var captions = [];
    var leadHeading = null;

    function move(node, dest) {
      moved.push({ node: node, parent: node.parentNode, next: node.nextSibling });
      dest.appendChild(node);
    }

    /* Off-screen slides sit in an overflow-clipped, translated track: a lazy
       <img> there would only start loading as it pans in. */
    function eagerLoad(img) {
      if (!img) return;
      eager.push({ img: img, value: img.getAttribute('loading') });
      img.setAttribute('loading', 'eager');
    }

    function restore() {
      for (var i = moved.length - 1; i >= 0; i--) {
        var m = moved[i];
        m.parent.insertBefore(m.node, m.next && m.next.parentNode === m.parent ? m.next : null);
      }
      moved.length = 0;
      eager.forEach(function (e) {
        if (e.value) e.img.setAttribute('loading', e.value);
        else e.img.removeAttribute('loading');
      });
      toArray(section.querySelectorAll('.lsm-car__snap, .lsm-car__slide, .lsm-car__caption')).forEach(function (el) {
        el.classList.remove('lsm-car__snap', 'lsm-car__slide', 'lsm-car__caption', 'lsm-car__caption--lead', 'lsm-car__snap--extra');
        el.removeAttribute('data-lsm-car-spot');
      });
      if (root.parentNode) root.parentNode.removeChild(root);
      section.classList.remove('lsm-car-on', 'lsm-car--expanded', 'lsm-car--fit');
    }

    try {
      found.collage.forEach(function (el, i) {
        el.classList.add('lsm-car__snap');
        if (i < SPOTS) {
          el.setAttribute('data-lsm-car-spot', String(i + 1));
          snaps.push(el);
          eagerLoad(el.querySelector('img'));
        } else {
          el.classList.add('lsm-car__snap--extra');
        }
        move(el, collageLayer);
      });

      slides.forEach(function (s, i) {
        s.el.classList.add('lsm-car__slide');
        eagerLoad(s.photo);
        move(s.el, track);
        if (s.layer) {
          s.layer.classList.add('lsm-car__caption');
          if (i === 0) s.layer.classList.add('lsm-car__caption--lead');
          move(s.layer, captionsLayer);
        }
        captions.push(s.layer);
      });
      if (slides[0].layer) leadHeading = slides[0].layer.querySelector('h1, h2, h3, h4, h5, h6');

      stage.appendChild(track);
      stage.appendChild(veil);
      root.appendChild(collageLayer);
      root.appendChild(stage);
      root.appendChild(captionsLayer);
      root.appendChild(peek);
      section.appendChild(root);
      section.classList.add('lsm-car-on');
    } catch (err) {
      restore();
      throw err;
    }

    /* Header height feeds the section height (the pinned box = the area under
       the header); the lead title is capped to the stage, never above the
       size staff set in the builder (read with the cap switched off). */
    function measure() {
      section.style.setProperty('--lsm-car-header', headerOffset() + 'px');
      if (leadHeading) {
        section.classList.remove('lsm-car--fit');
        var size = parseFloat(window.getComputedStyle(leadHeading).fontSize) || 88;
        section.style.setProperty('--lsm-car-lead-fs', size + 'px');
        section.classList.add('lsm-car--fit');
      }
    }
    measure();

    var scaleEndValue = 1;
    function scaleEnd() {
      var w = stage.offsetWidth || 1;
      var h = stage.offsetHeight || 1;
      scaleEndValue = Math.max(section.clientWidth / w, section.clientHeight / h) * 1.002;
      return scaleEndValue;
    }

    var expanded = false;
    function setExpanded(on) {
      if (on === expanded) return;
      expanded = on;
      section.classList.toggle('lsm-car--expanded', on);
    }

    try {
      ctx.mm.add({ mobile: '(max-width: 749px)', desktop: '(min-width: 750px)' }, function (mmCtx) {
        var mobile = !!mmCtx.conditions.mobile;
        measure();
        ScrollTrigger.addEventListener('refreshInit', measure);

        snaps.forEach(function (snap, i) {
          gsap.set(snap, { rotation: TILTS[i] });
        });

        /* 1 — approach (unpinned): snapshots drift in; ends where the pin starts. */
        if (snaps.length) {
          var approach = gsap.timeline({
            scrollTrigger: {
              trigger: section,
              start: 'top bottom',
              end: function () { return 'top ' + headerOffset() + 'px'; },
              scrub: true,
              invalidateOnRefresh: true
            }
          });
          snaps.forEach(function (snap, i) {
            var dir = i % 2 === 0 ? -1 : 1;
            approach.fromTo(snap, { y: dir * (20 + (i % 3) * 10) }, { y: 0, duration: 1, ease: 'none' }, 0);
          });
        }

        /* 2 — pinned master. Scroll cost per unit as % of the viewport:
           desktop 100 (morph) + 75 per slide, phones 70 + 55 with snapping to
           the resting states (0 = collage, 1 = slide 1 fullscreen, i = slide i). */
        var pinEnd = mobile ? 70 + 55 * (count - 1) : 100 + 75 * (count - 1);
        var master = gsap.timeline({
          scrollTrigger: {
            trigger: section,
            start: function () { return 'top ' + headerOffset() + 'px'; },
            end: '+=' + pinEnd + '%',
            scrub: true,
            pin: true,
            anticipatePin: 1,
            invalidateOnRefresh: true,
            snap: mobile ? {
              snapTo: 1 / count,
              directional: false,
              inertia: false,
              duration: { min: 0.15, max: 0.45 },
              delay: 0.05,
              ease: 'power1.inOut'
            } : undefined,
            onUpdate: function () {
              var s = gsap.getProperty(stage, 'scaleX');
              setExpanded(s >= 1 + (scaleEndValue - 1) * 0.85);
            }
          }
        });

        master.fromTo(stage, { scale: 1 }, { scale: scaleEnd, duration: 1, ease: 'none', transformOrigin: '50% 50%' }, 0);

        snaps.forEach(function (snap, i) {
          var right = i % 2 === 1;
          master
            .to(snap, {
              x: function () { return window.innerWidth * (right ? 0.15 : -0.2); },
              rotation: TILTS[i] + (right ? 10 : -10),
              duration: 1,
              ease: 'power1.in'
            }, 0)
            .to(snap, { opacity: 0, duration: 0.6, ease: 'none' }, 0);
        });

        master.fromTo(peek, { opacity: 0 }, { opacity: 1, duration: 0.25, ease: 'none' }, 0.75);
        master.to(track, { xPercent: -100 * (count - 1), duration: count - 1, ease: 'none' }, 1);

        captions.forEach(function (c, i) {
          if (c && i > 0) gsap.set(c, { opacity: 0 });
        });
        /* Out, then in — never two captions legible at once (see the original). */
        for (var i = 1; i < count; i++) {
          var boundary = i + 0.5;
          if (captions[i - 1]) {
            master.to(captions[i - 1], { opacity: 0, duration: SWAP, ease: 'none' }, boundary - SWAP);
          }
          if (captions[i]) {
            master.fromTo(captions[i], { opacity: 0 }, { opacity: 1, duration: SWAP, ease: 'none', immediateRender: false }, boundary);
          }
        }

        return function () {
          ScrollTrigger.removeEventListener('refreshInit', measure);
          setExpanded(false);
        };
      });
    } catch (err) {
      try { ctx.mm.revert(); } catch (e) { /* best effort */ }
      restore();
      throw err;
    }

    return null;
  });
})();
